import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../api'
import { useUserStore } from './userStore'
import type { UserProfileDto, UpdateUserProfileDto } from '../dtos'

export const useProfileStore = defineStore('profile', () => {
    const userStore = useUserStore()
    const profile = ref<UserProfileDto | null>(null)
    const loading = ref(false)

    async function fetchProfile() {
        if (!userStore.user) return
        loading.value = true
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get('/users/profile', {
                headers: { Authorization: `Bearer ${token}` }
            })
            profile.value = response.data
            applyTheme(response.data.theme)
        } catch (error) {
            console.error('Failed to fetch profile:', error)
        } finally {
            loading.value = false
        }
    }

    async function updateProfile(dto: UpdateUserProfileDto) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.put('/users/profile', dto, {
                headers: { Authorization: `Bearer ${token}` }
            })
            profile.value = response.data
            applyTheme(response.data.theme)
            return response.data
        } catch (error) {
            console.error('Failed to update profile:', error)
            throw error
        }
    }

    function applyTheme(theme: string) {
        const root = document.documentElement
        if (theme === 'dark') {
            root.classList.add('dark-mode')
        } else {
            root.classList.remove('dark-mode')
        }
    }

    async function uploadAvatar(file: Blob) {
        if (!userStore.user) return
        const token = await userStore.user.getIdToken()
        const formData = new FormData()
        formData.append('file', file, 'avatar.jpg')
        const response = await api.post('/users/profile/avatar', formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        })
        profile.value = response.data
        return response.data
    }

    async function deleteAvatar() {
        if (!userStore.user) return
        const token = await userStore.user.getIdToken()
        const response = await api.delete('/users/profile/avatar', {
            headers: { Authorization: `Bearer ${token}` }
        })
        profile.value = response.data
        return response.data
    }

    function clear() {
        profile.value = null
        loading.value = false
    }

    return { profile, loading, fetchProfile, updateProfile, uploadAvatar, deleteAvatar, applyTheme, clear }
})
